import React, { useEffect } from "react";
import Column from "components/ui/layout/column/column";
import CardBox from "components/ui/controls/card-box/card-box";
import Row from "components/ui/layout/row/row";
import { applyFormat } from "components/ui/ui-helpers";

// Composant pour un graphique en barres empilées (style "tube")
const StackedBarChart = ({ title, data, colors }) => {
  // Calcul de la valeur maximale totale pour normaliser les hauteurs
  const maxTotal = Math.max(...data.map((item) => item.obligatoire + item.facultatif + item.perteRevenu));

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        marginBottom: "20px",
        height: "500px",
      }}
    >
      <h3
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        {title}
      </h3>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-around",
          height: "400px",
          gap: "20px",
          paddingBottom: "40px",
        }}
      >
        {data.map((item, index) => {
          const total = item.obligatoire + item.facultatif + item.perteRevenu;
          const maxHeight = 300;

          // Calcul des hauteurs proportionnelles basées sur le total (hauteur du "tube")
          const totalHeight = maxTotal > 0 ? (total / maxTotal) * maxHeight : 20;
          const obligatoireHeight = total > 0 ? (item.obligatoire / total) * totalHeight : 0;
          const facultatifHeight = total > 0 ? (item.facultatif / total) * totalHeight : 0;
          const perteRevenuHeight = total > 0 ? (item.perteRevenu / total) * totalHeight : 0;

          // Calcul des pourcentages
          const obligatoirePct = total > 0 ? Math.round((item.obligatoire / total) * 100) : 0;
          const facultatifPct = total > 0 ? Math.round((item.facultatif / total) * 100) : 0;
          const perteRevenuPct = total > 0 ? Math.round((item.perteRevenu / total) * 100) : 0;

          return (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                maxWidth: "120px",
              }}
            >
              {/* Conteneur du "tube" avec fond transparent */}
              <div
                style={{
                  width: "80px",
                  height: `${totalHeight}px`,
                  position: "relative",
                  borderRadius: "8px",
                  border: "2px solid #e5e7eb",
                  backgroundColor: "rgba(229, 231, 235, 0.1)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "transform 0.2s ease",
                  marginTop: "20px",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                }}
              >
                {/* Contenu empilé du tube (de bas en haut) */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    right: "0",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column-reverse",
                  }}
                >
                  {/* Régime obligatoire (en bas) */}
                  {obligatoireHeight > 0 && (
                    <div
                      style={{
                        height: `${obligatoireHeight}px`,
                        background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[0]}cc 50%, ${colors[0]}99 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: "white",
                        fontWeight: "500",
                        position: "relative",
                        zIndex: 3,
                      }}
                    >
                      {obligatoirePct > 0 ? `${obligatoirePct}%` : ""}
                    </div>
                  )}

                  {/* Régime facultatif (au milieu) */}
                  {facultatifHeight > 0 && (
                    <div
                      style={{
                        height: `${facultatifHeight}px`,
                        background: `linear-gradient(135deg, ${colors[1]} 0%, ${colors[1]}cc 50%, ${colors[1]}99 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: "white",
                        fontWeight: "500",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      {facultatifPct > 0 ? `${facultatifPct}%` : ""}
                    </div>
                  )}

                  {/* Perte de revenu (partie vide du tube, en haut) */}
                  {perteRevenuHeight > 0 && (
                    <div
                      style={{
                        height: `${perteRevenuHeight}px`,
                        background: `linear-gradient(135deg, ${colors[2]}40 0%, ${colors[2]}20 50%, ${colors[2]}10 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: "#6b7280",
                        fontWeight: "500",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {perteRevenuPct > 0 ? `${perteRevenuPct}%` : ""}
                    </div>
                  )}
                </div>
              </div>

              {/* Label sous la barre */}
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#6b7280",
                  textAlign: "center",
                  marginTop: "8px",
                  lineHeight: "1.2",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant pour la légende du graphique empilé
const StackedLegend = ({ items }) => {
  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        marginBottom: "20px",
        height: "200px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <h4
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        Légende
      </h4>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "8px 12px",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "4px",
                backgroundColor: item.color,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            ></div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DetailInvaliditeTNS = ({ Outil, GraphInval, TabInval, Affichage }) => {
  useEffect(() => {
    document.getElementById("GraphInval").innerHTML = GraphInval;
  }, [GraphInval]);
  // Icônes pour les différentes catégories d'invalidité
  const getIconStyle = (category) => {
    if (category.includes("33%")) return { emoji: '%', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' };
    if (category.includes("66%")) return { emoji: '%', background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)' };
    if (category.includes("100%")) return { emoji: '%', background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' };
    return { emoji: '%', background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)' };
  };

  // Préparation des données pour le graphique empilé (ajouter la perte de revenus)
  const chartData = TabInval.map(data => {
    
    // Determine label based on category data
    const label = data.category ? data.category.replace(/<br>/g, ' ') : '';
    
    return {
      label: label,
      obligatoire: Math.max(0, data["Régime obligatoire"]),
      facultatif: Math.max(0, data["Régime facultatif"]),
      perteRevenu: Math.max(0, data["Perte de revenus"]),
    };
  });

  const chartColors = [
    "#3b82f6", // Régime obligatoire (bleu)
    "#10b981", // Régime facultatif (vert)
    "#9ca3af", // Perte de revenu (gris moderne)
  ];

  const legendItems = [
    { label: "Régime obligatoire", color: chartColors[0] },
    { label: "Régime facultatif", color: chartColors[1] },
    { label: "Perte de revenu", color: chartColors[2] },
  ];

  return (
    <Column fill>
      <CardBox>
        <Column fill>
          
          <Row bpad>
            <div className="cotisations-container">
              <div className="cotisations-list-container">
                <div className="cotisations-header">
                  <div className="header-column"></div>
                  <div className="header-column">Catégorie</div>
                  <div className="header-column">Régime(s) obligatoire(s)</div>
                  <div className="header-column">Régime(s) facultatif(s)</div>
                  <div className="header-column">Total</div>
                </div>
                
                {TabInval.map((data, i) => {
                  const iconStyle = getIconStyle(data.category || (i === 1 ? "33%" : i === 2 ? "66%" : i >= 3 ? "100%" : ""));
                  const category = i === 0 ? data.category : 
                                 i === 1 ? "33%" : 
                                 i === 2 ? "66%" : 
                                 i === 3 ? "100%" : 
                                 "100% + majo. tierce pers.";
                  
                  return (
                    <div key={i} className="cotisations-card">
                      <div 
                        className="cotisations-card-icon" 
                        style={{ background: iconStyle.background,fontSize:"10px" }}
                      >
                        {iconStyle.emoji}
                      </div>
                      <div className="card-column">{category}</div>
                      <div className="card-column">{applyFormat(Math.max(0, data["Régime obligatoire"]), "uint", { thousands: " ", decimal: ",", unit: " €" })}</div>
                      <div className="card-column">{applyFormat(Math.max(0, data["Régime facultatif"]), "uint", { thousands: " ", decimal: ",", unit: " €" })}</div>
                      <div className="card-column">{applyFormat(Math.max(0, data["Régime obligatoire"] + data["Régime facultatif"]), "uint", { thousands: " ", decimal: ",", unit: " €" })}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Row>          
          
          <Row style={{ gap: "20px", alignItems: "flex-start", marginTop: "20px" }}>
            <Column style={{ flex: 2 }}>
              <div style={{ position: "relative" }}>
                <StackedBarChart 
                  title="Prestations d'invalidité par catégorie"
                  data={chartData}
                  colors={chartColors}
                />
                {/* Container caché pour les données réelles */}
                <div id="GraphInval" style={{ display: "none" }}></div>
              </div>
            </Column>
            
            <Column style={{ flex: 1 }}>
              <StackedLegend items={legendItems} />
            </Column>
          </Row>
        </Column>
      </CardBox>
    </Column>
  );
};

export default DetailInvaliditeTNS;
